import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import { MentorsDTO } from '../../../../../apis/api.assignation'
import { useHRExpertMentors } from '../../../../../hooks/useHRExpertMentors'
import { useProfileStatus } from '../../../../../hooks/useProfileStatus'
import Modal from 'react-modal'
import styles from './ModalExpertMentorStatus.module.css'
import { colors } from '../../../../../styles/globals/_variables'
import { useLocation } from 'wouter'
import { useTeam } from '../../../Hooks/useTeam'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { useArrange } from '../../../../../hooks/ApiHooks/useRearrange'

export const ModalExpertMentorStatus = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean
    onClose?: (value: boolean) => void
}) => {
    const { t } = useTranslation('teamMonitor')
    const { mentors, refetch, isFetching } = useHRExpertMentors()

    return (
        <Modal isOpen={isOpen} style={modalStyles} onRequestClose={() => onClose?.(false)}>
            <FontAwesomeIcon
                icon={['fas', 'times']}
                onClick={() => onClose?.(false)}
                className={styles.closeIcon}
            />
            <section className={styles.modalSection}>
                <header>
                    <h4>{t('Expert Mentors')}</h4>
                </header>
                <main className={styles.profileList}>
                    <Section
                        title={t('Mentors available')}
                        mentors={mentors?.availables}
                        icon={['fas', 'circle-check']}
                        iconColor={colors.positiveGreen}
                        isFetching={isFetching}
                    />
                    <Section
                        title={t('Mentors busy')}
                        mentors={mentors?.busy}
                        icon={['fas', 'clock']}
                        iconColor={colors.eggColor}
                        isFetching={isFetching}
                    />
                    <Section
                        title={t('Mentors disconnected')}
                        mentors={mentors?.unavailable}
                        icon={['fas', 'ban']}
                        iconColor={colors.salmon}
                        isFetching={isFetching}
                    />
                </main>
                <footer>
                    <button onClick={() => refetch()} className="outlined" disabled={isFetching}>
                        {t('Refresh')}
                    </button>
                </footer>
            </section>
        </Modal>
    )
}

const Section = ({
    title,
    mentors,
    icon,
    iconColor,
    isFetching,
}: {
    title: string
    mentors?: MentorsDTO[]
    icon: IconProp
    iconColor: string
    isFetching?: boolean
}) => {
    const { t } = useTranslation('teamMonitor')
    if (!mentors) return null

    return (
        <section>
            <header>
                <h5>
                    <FontAwesomeIcon icon={icon} style={{ marginRight: 10, color: iconColor }} />
                    {title}: {isFetching ? '-' : mentors.length}
                </h5>
            </header>
            <main>
                {mentors.length === 0 && <div className={styles.noMentor}>{t('No mentors')}</div>}
                {mentors.map((mentor) => (
                    <ProfileItem key={mentor.profileId} {...mentor} />
                ))}
            </main>
        </section>
    )
}

const ProfileItem = ({ profileId, teamNumber, status }: MentorsDTO) => {
    const { t } = useTranslation('teamMonitor')
    const [, setLocation] = useLocation()
    const { courseID } = useTeam()
    const { data } = useProfileStatus(profileId)
    const inputRef = useRef<HTMLInputElement>(null)

    const [showAssignInput, setShowAssignInput] = useState(false)
    const [newTeamNumber, setAssignTo] = useState<number | undefined>()

    const { rearrange } = useArrange({ teamNumber, newTeamNumber })

    // Focus on input when showAssignInput is true
    useEffect(() => {
        if (showAssignInput) inputRef.current?.focus()
    }, [showAssignInput])

    const handleAssign = async (e?: FormEvent<HTMLFormElement>) => {
        e?.preventDefault()

        // Open input && clear newTeamNumber and focus on input
        if (!showAssignInput) {
            setAssignTo(undefined)
            setShowAssignInput(true)
            return
        }
        // Close input if newTeamNumber is empty
        if (!newTeamNumber && showAssignInput) {
            setShowAssignInput(false)
            return
        }

        if (!teamNumber || !newTeamNumber || teamNumber === newTeamNumber) return

        const message = t(`Are you sure you want to assign {{displayName}} to team {{team}}?`, {
            displayName: data?.displayName,
            team: newTeamNumber,
        })
        if (!confirm(message)) return

        setAssignTo(undefined)
        await rearrange({ _profileId: profileId }, teamNumber, newTeamNumber)

        setShowAssignInput(false)
    }

    // const isBusy = status === 'busy'
    const isAvailable = status === 'available'

    return (
        <div className={styles.profileItem}>
            <section>
                <span>{data?.displayName || profileId}</span>
            </section>

            <section className={styles.mentorActions}>
                {isAvailable && (
                    <form onSubmit={handleAssign}>
                        {showAssignInput && (
                            <input
                                type="number"
                                id="teamNumber"
                                placeholder={t('Team')}
                                className={styles.assignTeam}
                                value={newTeamNumber}
                                onChange={({ target: { value } }) =>
                                    setAssignTo(Number(Number(value)))
                                }
                                ref={inputRef}
                            />
                        )}
                        <button type="submit" className="icon" title={t('Assign to team')}>
                            <FontAwesomeIcon icon={['fas', 'people-arrows']} />
                        </button>
                    </form>
                )}

                {/* {isBusy && (
                    <button
                        className="icon"
                        title={t('Back to mentor team')}
                        onClick={handleBackToMentorTeam}
                    >
                        <FontAwesomeIcon icon={['fas', 'arrow-left-from-bracket']} />
                    </button>
                )} */}
                {teamNumber && (
                    <button
                        className="icon"
                        title={t('Show team')}
                        onClick={() =>
                            setLocation(
                                `/admin/team/${courseID}?searchType=teams&searchQuery=${teamNumber}`
                            )
                        }
                    >
                        {t('Team')} {teamNumber}
                    </button>
                )}
            </section>
        </div>
    )
}

export const modalStyles = {
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'var(--darkGreyEgg)',
        zIndex: 600001,
    },
    content: {
        inset: '50%',
        border: 0,
        background: 'rgb(255, 255, 255)',
        borderRadius: '8px',
        outline: 'none',
        overflow: 'hidden',
        padding: 20,
        width: 'fit-content',
        height: 'fit-content',
        transform: 'translate(-50%, -50%)',
    },
} as const
